import { BarcodeOutlined, DeleteOutlined, InfoCircleOutlined, MailOutlined, PhoneOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { antdTokenFilter, matchesTokens, tokenize } from '@medcom/shared-ui/utils/tokenSearch.ts';
import type { FieldData } from '@rc-component/form/lib/interface';
import {
  Form as AntForm,
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Dropdown,
  Empty,
  Flex,
  Form,
  type FormInstance,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Transfer,
  Typography,
  theme,
} from 'antd';
import { useWatch } from 'antd/es/form/Form';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { CodeEditor } from '../../../components/CodeEditor.tsx';
import { type FragmentType, getFragmentData } from '../../../graphql/generated';
import {
  AiPackage,
  AisExportType,
  AisImportType,
  type AssignedLabInput,
  BillingPreference,
  type DoctorAisLinkInput,
  type DoctorForm_InsuranceFragment,
  type DoctorForm_SpecialRateFragment,
  type DoctorInput,
  DoctorSalutation,
  type DoctorSpecialRateInput,
  TestTubeSupplier,
} from '../../../graphql/generated/graphql.ts';
import { tableActionCell } from '../../../styles/globalCss.ts';
import { API_DATE_FORMAT, SHORT_DATE_FORMAT } from '../../../utils/dateFormatUtils.ts';
import { ellipsis } from '../../../utils/ellipsis.ts';
import {
  translateAisExportType,
  translateAisImportType,
  translateBillingPreference,
  translateDoctorSalutation,
  translateTestTubeSupplier,
} from '../../../utils/enumHelpers';
import { serverSideEmailRule } from '../../../utils/helpers.ts';
import { LabelPreviewModal } from '../labelPreview/LabelPreviewModal.tsx';
import { PlaceholderHelp } from '../labelPreview/PlaceholderHelp.tsx';
import { DoctorAisLinksField } from './DoctorAisLinksField';
import { CatalogFragment, InsuranceFragment, LabFragment, SpecialRateFragment, TagFragment, ThemeFragment } from './DoctorForm.gql';
import { compareByTag } from './tagColors.ts';

const { Option, OptGroup } = Select;

// AI package tiers (LC-894). The monthly token quota is derived from the tier;
// CUSTOM uses the freely entered value (empty = unlimited, e.g. medcom/test doctors).
const AI_PACKAGE_TOKENS: Record<AiPackage, number | null> = {
  [AiPackage.TRIAL]: 2_000_000,
  [AiPackage.STARTER]: 2_000_000,
  [AiPackage.PRO]: 4_000_000,
  [AiPackage.BUSINESS]: 10_000_000,
  [AiPackage.CUSTOM]: null,
};

const AI_PACKAGE_OPTIONS = [
  { value: AiPackage.TRIAL, label: 'Trial (2M)' },
  { value: AiPackage.STARTER, label: 'Starter (2M)' },
  { value: AiPackage.PRO, label: 'Pro (4M)' },
  { value: AiPackage.BUSINESS, label: 'Business (10M)' },
  { value: AiPackage.CUSTOM, label: 'Custom' },
];

export type DoctorFormValues = DoctorInput & { aisLinks: DoctorAisLinkInput[] };

export const DoctorForm: React.FC<{
  allMeAddresses: string[];
  form: FormInstance<DoctorFormValues>;
  initialValues?: DoctorFormValues;
  onFinish: (values: DoctorFormValues) => void;
  onFieldsChange: (changedFields: FieldData[], allFields: FieldData[]) => void;
  labFragments: FragmentType<typeof LabFragment>[];
  catalogFragments: FragmentType<typeof CatalogFragment>[];
  insuranceFragments: FragmentType<typeof InsuranceFragment>[];
  specialRateFragments: FragmentType<typeof SpecialRateFragment>[];
  themeFragments: FragmentType<typeof ThemeFragment>[];
  tagFragments: FragmentType<typeof TagFragment>[];
}> = ({
  allMeAddresses,
  form,
  initialValues,
  onFinish,
  onFieldsChange,
  labFragments,
  catalogFragments,
  insuranceFragments,
  specialRateFragments,
  themeFragments,
  tagFragments,
}) => {
  const assignedLabs = useWatch<AssignedLabInput[]>('assignedLabs', { form, preserve: true });
  const aiEnabled = useWatch('aiEnabled', form);
  const aiPackage = useWatch('aiPackage', form);
  const labs = getFragmentData(LabFragment, labFragments);
  const themes = getFragmentData(ThemeFragment, themeFragments);
  const catalogs = getFragmentData(CatalogFragment, catalogFragments);
  const insurances = getFragmentData(InsuranceFragment, insuranceFragments);
  const allSpecialRates = getFragmentData(SpecialRateFragment, specialRateFragments);
  const tags = getFragmentData(TagFragment, tagFragments);

  const groupedCatalogs = useMemo(() => {
    const map = new Map<string, typeof catalogs>();
    for (const c of catalogs) {
      const key = c.lab?.shortName ?? 'Andere';
      const arr = map.get(key) ?? [];
      arr.push(c);
      map.set(key, arr);
    }
    // Sort catalogs within each group by name
    for (const [k, arr] of map.entries()) {
      arr.sort((a, b) => a.name.localeCompare(b.name));
      map.set(k, arr);
    }
    // Return a sorted array of entries by shortName
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [catalogs]);

  const { token } = theme.useToken();
  const [specialRateIdsToAdd, setSpecialRateIdsToAdd] = useState([]);

  // Billing-lab candidates = the doctor's assigned labs that have AI enabled (LC-894).
  const billingLabOptions = useMemo(() => {
    const assignedLabIds = new Set((assignedLabs ?? []).map(a => a.labId));
    return labs.filter(l => assignedLabIds.has(l.id) && l.aiEnabled).map(l => ({ value: l.id, label: l.name }));
  }, [assignedLabs, labs]);

  // Auto-select the billing lab when there is exactly one AI lab and none chosen yet.
  useEffect(() => {
    if (aiEnabled && billingLabOptions.length === 1 && !form.getFieldValue('aiBillingLabId')) {
      form.setFieldValue('aiBillingLabId', billingLabOptions[0].value);
    }
  }, [aiEnabled, billingLabOptions, form]);

  // Clear the billing lab if it is no longer an assigned AI lab (keeps the UI in sync with the
  // backend check, which rejects a billing lab that is not among the doctor's assigned labs).
  useEffect(() => {
    const current = form.getFieldValue('aiBillingLabId');
    if (current && !billingLabOptions.some(o => o.value === current)) {
      form.setFieldValue('aiBillingLabId', null);
    }
  }, [billingLabOptions, form]);

  const tooltip = {
    styles: { root: { width: 400, maxWidth: 400 } },
    overlay: <PlaceholderHelp labelType="DOCTOR" />,
    icon: <InfoCircleOutlined />,
  };

  const [preview, setPreview] = useState<{ zpl: string; width: number; height: number } | null>(null);

  return (
    <Form
      layout="vertical"
      form={form}
      onFinish={() => {
        const values = { ...form.getFieldsValue(true) };
        values.installDate = values.installDate?.format(API_DATE_FORMAT) ?? null;
        onFinish(values);
      }}
      onFieldsChange={onFieldsChange}
      initialValues={initialValues}
    >
      <Tabs
        defaultActiveKey="stammdaten"
        size="small"
        items={[
          {
            key: 'stammdaten',
            label: 'Stammdaten',
            forceRender: true,
            children: (
              <>
                <Row gutter={[16, 0]}>
                  <Col span={8}>
                    <Form.Item
                      name="salutation"
                      label="Anrede"
                      rules={[{ type: 'enum', enum: Object.keys(DoctorSalutation), required: true }]}
                    >
                      <Select autoFocus>
                        {Object.keys(DoctorSalutation).map(s => {
                          return (
                            <Option key={s} value={s}>
                              {translateDoctorSalutation(s)}
                            </Option>
                          );
                        })}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name="preTitle" label="Titel vorangestellt">
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name="postTitle" label="Titel nachgestellt">
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={[16, 0]}>
                  <Col span={12}>
                    <Form.Item name="firstName" label="Vorname">
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="lastName"
                      label="Nachname bzw. Firma"
                      tooltip="Relevant für z.B. HL7 und Dokumente"
                      rules={[{ type: 'string', required: true, whitespace: true }]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={[16, 0]}>
                  <Col span={12}>
                    <Form.Item
                      name="name"
                      label="Anzeigename"
                      tooltip="Wird im UI angezeigt"
                      rules={[{ type: 'string', required: true, whitespace: true }]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="specialist" label="Facharzt">
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={[16, 0]}>
                  <Col span={12}>
                    <Form.Item
                      name="meAddress"
                      label="ME-Adresse"
                      rules={[
                        {
                          validator: (_, value) => {
                            if (value && allMeAddresses.some(it => it === value)) {
                              return Promise.reject('Diese ME-Adresse ist bereits vergeben');
                            }
                            return Promise.resolve();
                          },
                          warningOnly: true,
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="hvNumber" label="Hauptverbandsnummer">
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={[16, 0]}>
                  <Col span={8}>
                    <Form.Item name="street" label="Straße inkl. Hausnummer" rules={[{ type: 'string', required: true, whitespace: true }]}>
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name="zip" label="PLZ" rules={[{ type: 'string', required: true, whitespace: true }]}>
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name="city" label="Ort" rules={[{ type: 'string', required: true, whitespace: true }]}>
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={[16, 0]}>
                  <Col span={12}>
                    <Form.Item name="email" label="E-Mail" rules={[serverSideEmailRule]} validateDebounce={200}>
                      <Input prefix={<MailOutlined />} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="phone" label="Telefon">
                      <Input prefix={<PhoneOutlined />} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name="installDate" label="Installationsdatum">
                      <DatePicker style={{ width: '100%' }} format={SHORT_DATE_FORMAT} />
                    </Form.Item>
                  </Col>
                  <Col span={16}>
                    <Form.Item name="tagIds" label="Tags">
                      <Select
                        mode="multiple"
                        allowClear
                        showSearch={{ filterOption: antdTokenFilter }}
                        placeholder="Tags auswählen"
                        tagRender={props => {
                          const tag = tags.find(t => t.id === props.value);
                          return (
                            <Tag color={tag?.color} closable onClose={props.onClose} style={{ marginInlineEnd: 4 }}>
                              {tag?.name}
                            </Tag>
                          );
                        }}
                      >
                        {[...tags].sort(compareByTag).map(tag => (
                          <Option key={tag.id} value={tag.id} search={tag.name}>
                            <Tag color={tag.color}>{tag.name}</Tag>
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item name="disabled" valuePropName="checked" noStyle>
                  <Checkbox>Deaktiviert</Checkbox>
                </Form.Item>
              </>
            ),
          },
          {
            key: 'kontakte',
            label: 'Kontakte',
            forceRender: true,
            children: (
              <Form.List name="phoneContacts">
                {(fields, { add, remove }) => (
                  <Space size="middle" orientation="vertical" style={{ width: '100%' }}>
                    <Button icon={<PlusCircleOutlined />} onClick={() => add({ name: '', phoneNumber: '' })}>
                      Kontakt hinzufügen
                    </Button>
                    <div>
                      {fields.map(({ key, name, ...restField }) => (
                        <Flex key={key} gap="middle" align="center">
                          <Form.Item
                            style={{ flexGrow: 1 }}
                            {...restField}
                            name={[name, 'name']}
                            label="Name / Bezeichnung"
                            rules={[{ required: true, whitespace: true, message: 'Bitte Name angeben' }]}
                          >
                            <Input placeholder="z.B. EDV Mitarbeiter" />
                          </Form.Item>
                          <Form.Item
                            style={{ flexGrow: 1 }}
                            {...restField}
                            name={[name, 'phoneNumber']}
                            label="Telefonnummer"
                            rules={[{ required: true, whitespace: true, message: 'Bitte Telefonnummer angeben' }]}
                          >
                            <Input placeholder="z.B. 0664 1231231" prefix={<PhoneOutlined />} />
                          </Form.Item>
                          <Button style={{ marginTop: '4px' }} icon={<DeleteOutlined />} type="text" danger onClick={() => remove(name)} />
                        </Flex>
                      ))}
                    </div>
                  </Space>
                )}
              </Form.List>
            ),
          },
          {
            key: 'billing',
            label: 'Kataloge & Verrechnung',
            forceRender: true,
            children: (
              <>
                <Form.Item name="catalogIds" label="Parameterkataloge">
                  <Select
                    showSearch={{ filterOption: antdTokenFilter }}
                    optionLabelProp="taglabel"
                    allowClear
                    mode="multiple"
                    placeholder="Bitte Parameterkataloge auswählen"
                  >
                    {groupedCatalogs.map(([shortName, group]) => (
                      <OptGroup key={shortName} label={shortName}>
                        {group.map(catalog => (
                          <Option
                            key={catalog.id}
                            value={catalog.id}
                            search={`${catalog.lab.shortName} ${catalog.name}`}
                            taglabel={`${catalog.lab.shortName} - ${catalog.name}`}
                          >
                            {catalog.name}
                          </Option>
                        ))}
                      </OptGroup>
                    ))}
                  </Select>
                </Form.Item>
                <Row gutter={[16, 0]}>
                  <Col span={12}>
                    <Form.Item /* fixes spacing*/>
                      <Space orientation="vertical">
                        <Form.Item name="patientBilling" valuePropName="checked" noStyle>
                          <Checkbox>Privat an Patient</Checkbox>
                        </Form.Item>
                        <Form.Item name="doctorBilling" valuePropName="checked" noStyle>
                          <Checkbox>Privat an Zuweiser</Checkbox>
                        </Form.Item>
                      </Space>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item /* fixes spacing*/>
                      <Space orientation="vertical">
                        <Form.Item name="showPrices" valuePropName="checked" noStyle>
                          <Checkbox>Preise anzeigen</Checkbox>
                        </Form.Item>
                        <Form.Item name="showPriceSuggestion" valuePropName="checked" noStyle>
                          <Checkbox>Preisvorschlag anzeigen</Checkbox>
                        </Form.Item>
                      </Space>
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={[16, 0]}>
                  <Col span={12}>
                    <Form.Item
                      name="billingPreference"
                      label="Präferenz Privatverrechnung"
                      rules={[{ type: 'enum', enum: Object.keys(BillingPreference) }]}
                    >
                      <Select>
                        {Object.keys(BillingPreference).map(bp => {
                          return (
                            <Option key={bp} value={bp}>
                              {translateBillingPreference(bp)}
                            </Option>
                          );
                        })}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Space orientation="vertical">
                      <Form.Item name="autoDiagnose" valuePropName="checked" noStyle>
                        <Checkbox>
                          Diagnose automatisch übernehmen <br />
                          <Typography.Text type="secondary">
                            <small>Wenn nur eine Diagnose verfügbar</small>
                          </Typography.Text>
                        </Checkbox>
                      </Form.Item>
                      <Form.Item name="autoSpecialRate" valuePropName="checked" noStyle>
                        <Checkbox>
                          Sondertarif automatisch übernehmen
                          <br />
                          <Typography.Text type="secondary">
                            <small>Wenn nur ein Sondertarif verfügbar</small>
                          </Typography.Text>
                        </Checkbox>
                      </Form.Item>
                      <Form.Item name="preferSpecialRate" valuePropName="checked" noStyle>
                        <Checkbox>Sondertarif hat Priorität ggü. Kasse</Checkbox>
                      </Form.Item>
                    </Space>
                  </Col>
                </Row>
                <Form.Item name="insuranceIds" label="Krankenkassen" valuePropName="targetKeys">
                  <Transfer<DoctorForm_InsuranceFragment>
                    dataSource={[...insurances].sort((a, b) => a.shortName.localeCompare(b.shortName))}
                    titles={['Nicht anforderbar', 'Anforderbar']}
                    render={insurance => (
                      <>
                        {insurance.shortName} <Typography.Text type="secondary">{insurance.code}</Typography.Text>
                      </>
                    )}
                    showSearch
                    filterOption={(inputValue, insurance) =>
                      matchesTokens(insurance, tokenize(inputValue), [i => i.name, i => i.shortName])
                    }
                    styles={{ section: { height: '300px', width: '100%', overflow: 'hidden' } }}
                    rowKey={insurance => insurance.id}
                  />
                </Form.Item>
                <Form.Item label="Sondertarife" shouldUpdate>
                  {() => {
                    const specialRateInputs: DoctorSpecialRateInput[] = form.getFieldsValue(true).specialRates ?? [];
                    const specialRateIds = specialRateInputs.map(it => it.specialRateId) ?? [];
                    const selectedSpecialRates = allSpecialRates.filter(it => specialRateIds.includes(it.id));
                    return (
                      <Space orientation="vertical" style={{ width: '100%' }}>
                        <Select
                          showSearch={{ filterOption: antdTokenFilter }}
                          mode="multiple"
                          allowClear
                          onChange={setSpecialRateIdsToAdd}
                          value={specialRateIdsToAdd}
                          onOpenChange={open => {
                            if (!open) {
                              form.setFieldsValue({
                                specialRates: [
                                  ...specialRateInputs,
                                  ...specialRateIdsToAdd.map(specialRateId => ({
                                    maxUsagesPerPeriod: null,
                                    specialRateId: specialRateId,
                                  })),
                                ],
                              });
                              setSpecialRateIdsToAdd([]);
                            }
                          }}
                          placeholder="Sondertarif hinzufügen"
                          optionLabelProp="taglabel"
                          style={{ width: '100%' }}
                          notFoundContent={<Empty description="Keine Sondertarife gefunden" image={Empty.PRESENTED_IMAGE_SIMPLE} />}
                          options={allSpecialRates
                            .filter(it => !specialRateIds.includes(it.id))
                            .sort((a, b) => a.shortName.localeCompare(b.shortName))
                            .map(it => ({
                              label: (
                                <>
                                  <div>{it.shortName}</div>
                                  <Typography.Text type="secondary">
                                    {it.lab.shortName}, {it.name}
                                  </Typography.Text>
                                </>
                              ),
                              search: `${it.shortName} ${it.name} ${it.lab.shortName}`,
                              taglabel: it.shortName,
                              value: it.id,
                            }))}
                        />
                        <Table<DoctorForm_SpecialRateFragment>
                          scroll={{ x: 'max-content' }}
                          rowKey={record => record.id}
                          size="small"
                          showSorterTooltip={false}
                          dataSource={selectedSpecialRates}
                          pagination={false}
                          columns={[
                            {
                              title: 'Kurzname',
                              dataIndex: 'shortName',
                              key: 'shortName',
                              ellipsis: true,
                              width: 110,
                              defaultSortOrder: 'ascend',
                              sorter: (a, b) => a.shortName.localeCompare(b.shortName),
                            },
                            {
                              title: 'Langbezeichnung',
                              dataIndex: 'name',
                              key: 'name',
                              ellipsis: true,
                              width: 160,
                              sorter: (a, b) => a.name.localeCompare(b.name),
                              render: v => ellipsis(v, 35),
                            },
                            {
                              title: 'Labor',
                              dataIndex: ['lab', 'shortName'],
                              key: 'lab',
                              width: 60,
                              ellipsis: true,
                              sorter: (a, b) => a.lab.shortName.localeCompare(b.lab.shortName),
                            },
                            {
                              title: 'Max. pro Monat',
                              key: 'maxUsagesPerPeriod',
                              width: 200,
                              render: (_, record) => (
                                <InputNumber
                                  placeholder="keine Einschränkung"
                                  value={specialRateInputs.find(it => it.specialRateId === record.id)?.maxUsagesPerPeriod ?? undefined}
                                  onChange={maxUsages => {
                                    form.setFieldValue(
                                      'specialRates',
                                      specialRateInputs.map(it =>
                                        it.specialRateId === record.id ? { ...it, maxUsagesPerPeriod: maxUsages } : it
                                      )
                                    );
                                  }}
                                  style={{ width: '100%' }}
                                />
                              ),
                            },
                            {
                              title: '',
                              key: 'actions',
                              fixed: 'right',
                              align: 'right',
                              ellipsis: true,
                              width: '50px',
                              className: tableActionCell,
                              render: (_, record) => (
                                <Button
                                  icon={<DeleteOutlined />}
                                  type="text"
                                  danger
                                  onClick={() =>
                                    form.setFieldValue(
                                      'specialRates',
                                      specialRateInputs.filter(it => it.specialRateId !== record.id)
                                    )
                                  }
                                />
                              ),
                            },
                          ]}
                          locale={{
                            emptyText: <Empty description="Fügen Sie Sondertarife hinzu" image={Empty.PRESENTED_IMAGE_SIMPLE} />,
                          }}
                        />
                      </Space>
                    );
                  }}
                </Form.Item>
              </>
            ),
          },
          {
            key: 'lg',
            label: 'LG Verrechnung',
            forceRender: true,
            children: (
              <>
                <Form.Item name="lgDoctorBilling" valuePropName="checked">
                  <Checkbox>Privat an Zuweiser</Checkbox>
                </Form.Item>
                <Form.Item name="lgInsuranceIds" label="Krankenkassen" valuePropName="targetKeys">
                  <Transfer<DoctorForm_InsuranceFragment>
                    dataSource={[...insurances].sort((a, b) => a.shortName.localeCompare(b.shortName))}
                    titles={['Nicht anforderbar', 'Anforderbar']}
                    render={insurance => (
                      <>
                        {insurance.shortName} <Typography.Text type="secondary">{insurance.code}</Typography.Text>
                      </>
                    )}
                    showSearch
                    filterOption={(inputValue, insurance) =>
                      matchesTokens(insurance, tokenize(inputValue), [i => i.name, i => i.shortName])
                    }
                    styles={{ section: { height: '300px', width: '100%', overflow: 'hidden' } }}
                    rowKey={insurance => insurance.id}
                  />
                </Form.Item>
              </>
            ),
          },
          {
            key: 'labors',
            label: 'Labors',
            forceRender: true,
            children: (
              <Form.List
                name="assignedLabs"
                rules={[
                  {
                    validator: (_, values) => {
                      if (!values || values.length < 1) {
                        return Promise.reject('Mind. ein Labor angeben');
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                {(fields, { add, remove }, { errors }) => (
                  <Space orientation="vertical" style={{ width: '100%' }}>
                    <Dropdown
                      menu={{
                        items: labs.map(lab => ({
                          key: lab.id,
                          label: lab.name,
                          disabled: assignedLabs?.map(al => al.labId)?.includes(lab.id) ?? false,
                          onClick: () =>
                            add({
                              labId: lab.id,
                              customerId: '',
                              supplier: TestTubeSupplier.VACUETTE,
                              runningNumber: 0,
                              overrideRunningNumber: false,
                            }),
                        })),
                      }}
                      placement="bottomLeft"
                      trigger={['click']}
                    >
                      <Button icon={<PlusCircleOutlined />}>Labor hinzufügen</Button>
                    </Dropdown>
                    {fields.map(({ key, name, ...restField }, index) => (
                      <Card
                        size="small"
                        title={labs.find(l => l.id === (assignedLabs ?? [])[index]?.labId)?.name ?? `Labor ${index + 1}`}
                        extra={<Button danger size="small" icon={<DeleteOutlined />} type="text" onClick={() => remove(index)} />}
                        style={{ width: '100%' }}
                        key={key}
                      >
                        <Row gutter={[16, 0]}>
                          <Col span={8}>
                            <Form.Item
                              {...restField}
                              name={[name, 'runningNumber']}
                              rules={[{ type: 'integer', required: true, min: 0, message: 'Bitte Zähler angeben' }]}
                              label="Laufende Auftragsnummer"
                            >
                              <InputNumber
                                style={{ width: '100%' }}
                                inputMode="numeric"
                                precision={0}
                                min={0}
                                step={1}
                                disabled={!(assignedLabs ?? [])[index]?.overrideRunningNumber}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={8}>
                            <Form.Item {...restField} name={[name, 'customerId']} label="Kundennummer">
                              <Input />
                            </Form.Item>
                          </Col>
                          <Col span={8}>
                            <Form.Item
                              {...restField}
                              name={[name, 'supplier']}
                              label="Lieferant"
                              rules={[{ type: 'enum', enum: Object.keys(TestTubeSupplier) }]}
                            >
                              <Select>
                                {Object.keys(TestTubeSupplier).map(supplier => (
                                  <Option key={supplier} value={supplier}>
                                    {translateTestTubeSupplier(supplier)}
                                  </Option>
                                ))}
                              </Select>
                            </Form.Item>
                          </Col>
                        </Row>
                        <Form.Item {...restField} name={[name, 'overrideRunningNumber']} valuePropName="checked">
                          <Checkbox>Laufende Auftragsnummer überschreiben</Checkbox>
                        </Form.Item>
                      </Card>
                    ))}
                    <Form.ErrorList errors={errors} />
                  </Space>
                )}
              </Form.List>
            ),
          },
          {
            key: 'io',
            label: 'I/O',
            forceRender: true,
            children: (
              <>
                <Row gutter={[16, 0]}>
                  <Col span={8}>
                    <Form.Item name="hasAis" label="AIS" valuePropName="checked">
                      <Checkbox>AIS aktiviert</Checkbox>
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name="aisImportType" label="AIS-Import-Typ" rules={[{ type: 'enum', enum: Object.keys(AisImportType) }]}>
                      <Select>
                        {Object.keys(AisImportType).map(type => (
                          <Option key={type} value={type}>
                            {translateAisImportType(type)}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      name="aisImportDoctorId"
                      label="AIS-Import Multi-Seat"
                      tooltip="Die mit diesem Feature verlinkten Zuweiser sollten alle die gleiche AIS-Schnittstelle verwenden, um Inkonsistenzen beim Lesen der Import-Datei zu vermeiden"
                    >
                      <Input placeholder="X-Arzt" />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={[16, 0]}>
                  <Col span={8}>
                    <Form.Item name="printTransferDocument" label="Überweisungsschein" valuePropName="checked">
                      <Checkbox>Beim Senden drucken</Checkbox>
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name="aisExportTypes" label="AIS-Export-Typen">
                      <Select mode="multiple" showSearch={false} placeholder="Bitte wählen...">
                        {Object.keys(AisExportType).map(type => (
                          <Option key={type} value={type}>
                            {translateAisExportType(type)}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      name="addEmptyLabels"
                      label="Zusätzliche Leeretiketten"
                      tooltip="Anzahl der zusätzlichen Leeretiketten, die pro Anforderung automatisch gedruckt werden"
                      rules={[
                        {
                          type: 'integer',
                          required: true,
                          min: 0,
                          max: 15,
                          message: 'Bitte Anzahl zw. 0 und 15 angeben',
                        },
                      ]}
                    >
                      <InputNumber style={{ width: '100%' }} inputMode="numeric" precision={0} max={15} min={0} step={1} />
                    </Form.Item>
                  </Col>
                </Row>
                <DoctorAisLinksField />
                <Row gutter={[16, 0]}>
                  <Col span={16}>
                    <Form.Item
                      name="zplLabelTemplate"
                      label={
                        <div>
                          <span>ZPL-Etiketten-Vorlage</span>{' '}
                          <Button
                            size="small"
                            icon={<BarcodeOutlined />}
                            onClick={() =>
                              setPreview({
                                zpl: form.getFieldValue('zplLabelTemplate'),
                                width: form.getFieldValue('zplLabelWidth'),
                                height: form.getFieldValue('zplLabelHeight'),
                              })
                            }
                          >
                            Vorschau
                          </Button>
                        </div>
                      }
                      tooltip={tooltip}
                    >
                      <CodeEditor mode="zplDoctorLabel" height="300px" />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      name="addZplLabels"
                      label="Zusätzliche Zuweiser-Etiketten"
                      tooltip="Anzahl der zusätzlichen Zuweiser-Etiketten, die pro Anforderung automatisch gedruckt werden"
                      rules={[
                        {
                          type: 'integer',
                          required: true,
                          min: 0,
                          max: 15,
                          message: 'Bitte Anzahl zw. 0 und 15 angeben',
                        },
                      ]}
                    >
                      <InputNumber style={{ width: '100%' }} inputMode="numeric" precision={0} max={15} min={0} step={1} />
                    </Form.Item>
                    <Form.Item
                      name="zplLabelWidth"
                      label="Breite ZPL-Etikett in mm für Vorschau"
                      rules={[{ type: 'integer', required: true, min: 10, max: 100, message: 'Bitte Zahl angeben' }]}
                    >
                      <InputNumber inputMode="numeric" required min={10} max={100} step={1} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item
                      name="zplLabelHeight"
                      label="Höhe ZPL-Etikett in mm für Vorschau"
                      rules={[{ type: 'integer', required: true, min: 10, max: 100, message: 'Bitte Zahl angeben' }]}
                    >
                      <InputNumber inputMode="numeric" required min={10} max={100} step={1} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                </Row>
              </>
            ),
          },
          {
            key: 'theme',
            label: 'Theme',
            forceRender: true,
            children: (
              <Row gutter={[16, 0]}>
                <Col span={8}>
                  <Form.Item name="themeId" label="Theme">
                    <Select
                      placeholder="Theme zuweisen"
                      style={{ width: '300px' }}
                      allowClear
                      showSearch={{ filterOption: antdTokenFilter }}
                    >
                      {themes.map(it => {
                        return (
                          <Option key={it.id} value={it.id} search={it.name}>
                            {it.name}
                          </Option>
                        );
                      })}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="flipParamNames"
                    valuePropName="checked"
                    label="Parameter-Namen"
                    tooltip="Kurzname - Langbezeichnung bzw. Langbezeichnung - Kurzname"
                  >
                    <Checkbox>umdrehen</Checkbox>
                  </Form.Item>
                </Col>
              </Row>
            ),
          },
          {
            key: 'comment',
            label: 'Anmerkungen',
            forceRender: true,
            children: (
              <Form.Item name="comment" label="Interne Anmerkung">
                <Input.TextArea rows={10} />
              </Form.Item>
            ),
          },
          {
            key: 'features',
            label: 'Features',
            forceRender: true,
            children: (
              <Flex vertical gap={10}>
                <AntForm.Item name="csvImportEnabled" valuePropName="checked" noStyle>
                  <Checkbox>CSV-Import</Checkbox>
                </AntForm.Item>
                <AntForm.Item name="ecardEnabled" valuePropName="checked" noStyle>
                  <Checkbox>ecard-Integration</Checkbox>
                </AntForm.Item>
                <AntForm.Item name="aiEnabled" valuePropName="checked" noStyle>
                  <Checkbox>KI-Assistent</Checkbox>
                </AntForm.Item>
                {aiEnabled && (
                  <Flex vertical gap={token.paddingXS} style={{ paddingLeft: token.paddingLG }}>
                    <AntForm.Item name="aiPackage" label="KI-Paket" style={{ marginBottom: 0 }}>
                      <Select
                        placeholder="Paket wählen"
                        style={{ width: 250 }}
                        options={AI_PACKAGE_OPTIONS}
                        onChange={(pkg: AiPackage) => {
                          const tokens = AI_PACKAGE_TOKENS[pkg];
                          if (tokens != null) {
                            form.setFieldValue('aiMonthlyTokenQuota', tokens);
                          }
                        }}
                      />
                    </AntForm.Item>
                    <AntForm.Item
                      name="aiMonthlyTokenQuota"
                      label="Kontingent / Monat"
                      tooltip="Hartes Limit pro Kalendermonat. Bei festen Paketen aus dem Paket abgeleitet, bei Custom frei (leer = unbegrenzt). Reset zum Monatsanfang."
                      style={{ marginBottom: 0 }}
                    >
                      <InputNumber
                        min={0}
                        step={100000}
                        style={{ width: 250 }}
                        placeholder={aiPackage === AiPackage.CUSTOM ? 'unbegrenzt' : ''}
                        disabled={aiPackage !== AiPackage.CUSTOM}
                        suffix="Tokens"
                      />
                    </AntForm.Item>
                    <AntForm.Item
                      name="aiBillingLabId"
                      label="Abrechnendes Labor"
                      tooltip="Labor, das das KI-Paket dieses Zuweisers zahlt. Bei festen Paketen erforderlich; bei Custom optional (leer = intern, nicht abgerechnet)."
                      style={{ marginBottom: 0 }}
                      rules={
                        aiPackage && aiPackage !== AiPackage.CUSTOM
                          ? [
                              {
                                required: true,
                                message: 'Bei einem festen Paket ist ein abrechnendes Labor erforderlich.',
                              },
                            ]
                          : []
                      }
                    >
                      <Select
                        allowClear
                        placeholder={billingLabOptions.length ? 'Labor wählen' : 'Kein AI-Labor zugewiesen'}
                        disabled={!billingLabOptions.length}
                        options={billingLabOptions}
                        style={{ width: 250 }}
                      />
                    </AntForm.Item>
                  </Flex>
                )}
                <AntForm.Item name="overrideRequestEnabled" valuePropName="checked" noStyle>
                  <Checkbox>Offene Anforderung überschreiben</Checkbox>
                </AntForm.Item>
              </Flex>
            ),
          },
        ]}
      />
      {/* hack for enter button support */}
      <Button htmlType="submit" hidden />
      <LabelPreviewModal
        open={!!preview}
        onClose={() => setPreview(null)}
        labelType="DOCTOR"
        zpl={preview?.zpl ?? ''}
        width={preview?.width ?? 0}
        height={preview?.height ?? 0}
      />
    </Form>
  );
};
